import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import React from "react";

export type DropdownMenuItemProps = {
    type: "item";
    id: string;
    label: string;
    icon?: React.ReactNode;
    className?: string;
    onClick?: () => void;
};

export type DropdownMenuDividerProps = {
    type: "divider";
    id: string;
};

export type DropdownMenuHeaderProps = {
    type: "header";
    id: string;
    label: string;
};

export type DropdownMenuEntry =
    | DropdownMenuItemProps
    | DropdownMenuDividerProps
    | DropdownMenuHeaderProps;

export function DropdownMenu({
    items,
    selectedOptionId,
    className,
    placeholder = "Select...",
}: {
    items: DropdownMenuEntry[];
    selectedOptionId: string;
    className?: string;
    placeholder?: string;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [isPositioned, setIsPositioned] = useState(false);

    const toggleMenu = () => {
        if (isOpen) {
            setIsPositioned(false);
        }
        setIsOpen(!isOpen);
    };

    useEffect(() => {
        if (isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setMenuPosition({
                top: rect.bottom + 4,
                left: rect.left,
            });
            setIsPositioned(true);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
                setIsPositioned(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    const selectedItem = items.find(
        (item) => item.type === "item" && item.id === selectedOptionId,
    ) as DropdownMenuItemProps | undefined;

    return (
        <div className=" ">
            <div className="relative">
                <div
                    ref={buttonRef}
                    onClick={toggleMenu}
                    className={clsx(
                        "flex w-fit cursor-pointer items-center justify-between gap-4 rounded-md border border-slate-600 px-4 py-1",
                        "subdued transition-colors hover:text-gray-300",
                        className || "body-s",
                    )}
                >
                    <span className="flex flex-1 items-center gap-2">
                        {selectedItem?.icon}
                        {selectedItem?.label || placeholder}
                    </span>
                    <ChevronDownIcon
                        className={clsx(
                            "size-3 transition-transform duration-200",
                            isOpen && "rotate-180",
                        )}
                    />
                </div>
                {isOpen &&
                    createPortal(
                        <div
                            ref={menuRef}
                            style={{
                                position: "fixed",
                                top: `${menuPosition.top}px`,
                                left: `${menuPosition.left}px`,
                                opacity: isPositioned ? 1 : 0,
                            }}
                            className={clsx(
                                "bg-slate-800 pr-3",
                                "rounded-lg shadow-400",
                                "custom-scrollbar max-h-[400px] overflow-auto p-2",
                                "min-w-[160px]",
                                "flex flex-col gap-1",
                                "z-[10000000]",
                            )}
                        >
                            {items.map((entry, index) => (
                                <React.Fragment key={entry.id}>
                                    {entry.type === "divider" && (
                                        <div
                                            key={entry.id}
                                            className="my-1 h-px bg-slate-600"
                                        />
                                    )}
                                    {entry.type === "header" && (
                                        <div
                                            key={entry.id}
                                            className={clsx(
                                                "mb-1 px-2 py-1.5",
                                                "bg-default-overlay-7 lozenge subdued",
                                                index !== 0 && "mt-2",
                                            )}
                                        >
                                            {entry.label}
                                        </div>
                                    )}
                                    {entry.type === "item" && (
                                        <button
                                            key={entry.id}
                                            onClick={() => {
                                                entry.onClick?.();
                                                setIsOpen(false);
                                            }}
                                            className={clsx(
                                                "flex h-[36px] w-full items-center gap-2 rounded-md px-2 py-1.5",
                                                "menu-item body-micro cursor-pointer hover:bg-slate-600",
                                                selectedOptionId === entry.id &&
                                                    "bg-selected",
                                            )}
                                            role="menuitem"
                                        >
                                            {entry.icon}
                                            {entry.label}
                                        </button>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>,
                        document.body,
                    )}
            </div>
        </div>
    );
}
